import { Icon } from '@mui/material';
import arrayToTree from 'array-to-tree';
import clsx from 'clsx';
import DndTable from 'global/components/DragTable';
import { Row } from 'global/components/row';
import SearchBar from 'global/components/SearchBar';
import useDebounce from 'global/hooks/useDebounce';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import { get, set } from 'global/lib/lodash';
import { Loader, useScreen } from 'global/styles';
import { convertToPostStructure } from 'global/utils';
import * as moment from 'moment';
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import sendData from 'redux/actions/sendData';
import { realmsToString } from '../../../global/utils';
import { EditScorecard } from './edit-scorecard';
import { FullScreen } from 'global/components/modal';
import useStyles from './styles';
import SnackAlerts from 'global/components/snack-alerts';

export const StatusTypes = [
  { title: 'Archived', value: 'archived' },
  { title: 'Active', value: 1 },
  { title: 'Draft', value: 'draft' },
  { title: 'Shared', value: 'shared' },
];

export default withRouter((props) => {
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    status: StatusTypes,
    statusValue: 0,
    typeValue: 0,
    selected: {},
    loading: false,
    search: '',
  });

  const [rms, setRms] = useState({
    modalProps: {
      open: false,
      onClose: () => {
        setRms((s) => ({ ...s, modalProps: { ...s.modalProps, open: false } }));
      },
    },
  });
  const { me } = useUser();

  const [createProps, setCreateProps] = useState({
    open: false,
    onClose: (updated) => {
      setCreateProps((s) => ({ ...s, centerContentStyle: {}, open: false }));
    },
  });

  const debSearch = useDebounce(state.search, 700);
  const [params, setParams] = useState({
    page: { size: 500, number: 1 },
    include: 'userGroups,realms,edition,edition.realms',
  });

  const transformData = (d) => {
    const myRealmId = Number(get(me, 'realm.id'));

    d = d.map((item) => {
      const latestId = item.latestId === Number(item.id) ? null : item.latestId;
      const isOwnerRealm = item.ownerRealmId === myRealmId;

      return {
        ...item,
        latestId,
        isShared: !isOwnerRealm,
        realms: isOwnerRealm ? item.realms : [{ tier: 'shared', name: 'Shared' }],
      };
    });
    let treed = arrayToTree(d, { parentProperty: 'parentId' });

    treed = treed.map((tr) => {
      const all = [];

      const loopChildren = (arr, parent) => {
        arr.map((t) => {
          all.push({ ...t, parent });
          if (t.children) {
            loopChildren(t.children, t);
          }
        });
      };

      loopChildren(tr.children || []);

      tr.children = all;
      return tr;
    });

    let total = [];

    treed.map((tr) => {
      const trall = [tr, ...(tr.children || [])].map((t) => ({ ...t, parent: tr })).reverse();
      const top = trall.find((t) => t.id == tr.latestId);

      if (top) {
        delete top.parent;
        total = [...total, top, ...trall.filter((t) => t.id != top.id)];
      } else {
        total = [...total, tr];
      }
    });

    // Show shared scorecards at the bottom of the list
    const shared = [];
    const unshared = [];
    total.forEach((row) => {
      (row.isShared ? shared : unshared).push(row);
    });

    return [...unshared, ...shared];
  };

  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    {
      build: ['scorecards'],
      useEffect: true,
      params,
      transformData,
    }
  );

  const userGroups = useRetrieveData({ url: '/user-groups' }, { build: ['userGroups'] });

  const { data } = scorecards;

  useEffect(() => {
    setParams((prev) => {
      const next = {
        ...prev,
        page: { size: 500, number: 1 },
        filter: { ...(prev.filter || {}) },
      };

      if (debSearch) {
        set(next, 'filter.name', debSearch);
      } else {
        if (get(next, 'filter.name')) {
          delete next.filter.name;
        }
      }

      // Clean up empty filter object
      if (next.filter && Object.keys(next.filter).length === 0) {
        delete next.filter;
      }

      // Prevent triggering a fetch if nothing meaningful changed.
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [debSearch]);

  const handleSubmit = async (room_checkpoints, scdata) => {
    const config = get(scdata, 'data.config', {});
    const clientVendorId = get(scdata, 'data.clientVendorId', null);

    if (!clientVendorId) {
      setAlerts({ message: 'No client vendor associated with this scorecard', severity: 'error' });
      return;
    }

    const data = {
      data: {
        id: clientVendorId.toString(),
        type: 'client-vendors',
        scorecards: {
          id: get(scdata, 'data.id').toString(),
          type: 'scorecards',
          config: { ...convertToPostStructure(config), room_checkpoints },
        },
      },
    };

    await sendData({
      url: `/client-vendors/${clientVendorId.toString()}`,
      method: 'patch',
      data,
    });

    createProps.onClose(true);
  };

  const selectRow = (row) => {
    setRms((s) => ({
      ...s,
      modalProps: {
        ...s.modalProps,
        open: true,
      },
      scorecard: row,
      userGroups: userGroups.data,
    }));
    editScorecard(row);
  };

  const editScorecard = (sc) => {
    setCreateProps((s) => ({
      ...s,
      open: true,
      onClose: (updated) => {
        setCreateProps((s) => ({ ...s, centerContentStyle: {}, open: false }));
        updated && scorecards.update();
      },
      scorecard: sc,
      edition: sc.edition,
      userGroups: userGroups.data,
      Component: EditScorecard,
      centerContentStyle: { padding: 0 },
      title: `Scorecard Information`,
      ...(sc?.isShared ? { onSubmit: handleSubmit } : { onSubmit: undefined }),
    }));
  };

  return state.loading ? (
    <div className={clsx(classes.container, classes.flexRow, classes.pad20)}>
      <Loader />
    </div>
  ) : (
    <div className={classes.container}>
      <div>
        <SearchBar changeSearch={(t) => setState((s) => ({ ...s, search: t }))} containerStyle={classes.searchBar} />
      </div>

      <div className={classes.table}>
        {scorecards.loading && (
          <div className={clsx(classes.flexRow, classes.pad20)}>
            <Loader />
          </div>
        )}
        {!scorecards.loading && <DndTable columns={COLUMNS} canDrag={false} onSelectRow={selectRow} items={data} />}
      </div>
      <FullScreen {...createProps}>{!!createProps.open && !!createProps.Component && <createProps.Component {...createProps} doc={{ ...data, scorecards: rms.scorecard }} />}</FullScreen>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
    </div>
  );
});

const COLUMNS = [
  {
    field: 'name',
    title: 'Title',
    width: 200,
    render: (row) => {
      return (
        <Row>
          {row.parent && <Icon className="parentIcon">{'keyboard_return'}</Icon>}
          {get(row, 'name')}
        </Row>
      );
    },
  },
  {
    field: 'realm',
    title: 'Access',
    width: 250,
    render: (row, text) => {
      return (
        <div>
          <Row style={{ flexWrap: 'wrap' }}>{row.isShared ? <em>{realmsToString(row.realms, { includeIcon: true })}</em> : realmsToString(row.realms, { includeIcon: true })}</Row>
          <div>{(get(row, 'userGroups', []) || []).map((u) => u.name).join(', ')}</div>
        </div>
      );
    },
  },
  {
    field: 'createdAt',
    title: 'Created',
    width: 100,
    render: (row, text) => {
      return moment(text).format('ll');
    },
  },
];
